import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError, api } from '../api/client.js'
import { useToast } from '../components/toast.js'
import { Modal, Notice, Spinner } from '../components/ui.js'

interface Impact {
  name: string
  openTasks: number
  completedTasks: number
  plans: number
  reportedIssues: number
}

/**
 * Deleting a staff member is not reversible and touches the proof report, so the
 * dialog states exactly what will happen before the button is armed.
 */
export function DeleteStaffModal({
  staffId,
  onClose,
  onDeleted,
}: {
  staffId: string | null
  onClose: () => void
  onDeleted: () => void
}) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [anonymize, setAnonymize] = useState(false)
  const [confirmText, setConfirmText] = useState('')

  const { data: impact, isLoading } = useQuery({
    queryKey: ['staff-impact', staffId],
    queryFn: () => api<Impact>(`/api/staff/${staffId}/deletion-impact`),
    enabled: Boolean(staffId),
  })

  const remove = useMutation({
    mutationFn: () => api(`/api/staff/${staffId}?anonymize=${anonymize}`, { method: 'DELETE' }),
    onSuccess: async () => {
      toast(`${impact?.name ?? 'Mitarbeiter'} wurde gelöscht.`)
      setConfirmText('')
      setAnonymize(false)
      await queryClient.invalidateQueries({ queryKey: ['staff'] })
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      await queryClient.invalidateQueries({ queryKey: ['occurrences'] })
      onDeleted()
    },
    onError: (error) => toast(error instanceof ApiError ? error.message : 'Löschen fehlgeschlagen.', 'error'),
  })

  const armed = confirmText.trim().toLowerCase() === 'löschen'

  return (
    <Modal
      open={staffId !== null}
      title="Mitarbeiter endgültig löschen"
      onClose={() => {
        setConfirmText('')
        onClose()
      }}
      footer={
        <>
          <button
            type="button"
            className="btn secondary"
            onClick={() => {
              setConfirmText('')
              onClose()
            }}
          >
            Abbrechen
          </button>
          <button
            type="button"
            className="btn danger"
            disabled={!armed || remove.isPending}
            onClick={() => remove.mutate()}
          >
            {remove.isPending ? 'Wird gelöscht …' : 'Endgültig löschen'}
          </button>
        </>
      }
    >
      {isLoading || !impact ? (
        <Spinner />
      ) : (
        <>
          <Notice kind="error">
            <strong>{impact.name}</strong> wird vollständig entfernt, aus der Mitarbeiterliste, der Übersicht und
            allen Auswahlfeldern. Das lässt sich nicht rückgängig machen.
          </Notice>

          <table className="data" style={{ marginBottom: 16 }}>
            <tbody>
              <tr>
                <td className="muted">Offene Aufgaben</td>
                <td className="strong">
                  {impact.openTasks}
                  {impact.openTasks > 0 && (
                    <div className="muted small">
                      verlieren die Zuweisung und müssen neu vergeben werden
                    </div>
                  )}
                </td>
              </tr>
              <tr>
                <td className="muted">Aufgabenpläne</td>
                <td className="strong">
                  {impact.plans}
                  {impact.plans > 0 && (
                    <div className="muted small">laufen weiter, aber ohne zuständige Person</div>
                  )}
                </td>
              </tr>
              <tr>
                <td className="muted">Erledigte Aufgaben</td>
                <td className="strong">
                  {impact.completedTasks}
                  {impact.completedTasks > 0 && (
                    <div className="muted small">
                      bleiben im Nachweis erhalten, der Name ist dort gespeichert
                    </div>
                  )}
                </td>
              </tr>
              <tr>
                <td className="muted">Gemeldete Mängel</td>
                <td className="strong">{impact.reportedIssues}</td>
              </tr>
            </tbody>
          </table>

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 9, marginBottom: 16 }}>
            <input
              type="checkbox"
              checked={anonymize}
              onChange={(event) => setAnonymize(event.target.checked)}
              style={{ width: 18, height: 18, marginTop: 2 }}
            />
            <span>
              Namen auch aus vergangenen Nachweisen entfernen
              <div className="muted small">
                Dort steht dann „Ehemaliger Mitarbeiter“. Nur wählen, wenn das ausdrücklich verlangt wird, der
                Nachweis verliert dadurch an Aussagekraft.
              </div>
            </span>
          </label>

          <label className="field">
            <span>
              Zum Bestätigen <strong>löschen</strong> eintippen
            </span>
            <input
              value={confirmText}
              onChange={(event) => setConfirmText(event.target.value)}
              placeholder="löschen"
              autoComplete="off"
            />
          </label>
        </>
      )}
    </Modal>
  )
}
